import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { customerApi, stitchItemApi, shopSettingsApi as settingsApi, authApi } from '../services/api';
import { Customer, PaginationInfo, PaginationParams, StitchItem, MeasurementDefinition, ShopSettings, ShopSettingsFormData } from '../types';
import Pagination from '../components/Pagination';
import SubscriptionDisplay from '../components/SubscriptionDisplay';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [deletedCustomers, setDeletedCustomers] = useState<Customer[]>([]);
  const [stitchItems, setStitchItems] = useState<StitchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Stitch Items state
  const [showStitchItemDialog, setShowStitchItemDialog] = useState(false);
  const [editingStitchItem, setEditingStitchItem] = useState<StitchItem | null>(null);
  const [stitchItemForm, setStitchItemForm] = useState({
    name: '',
    base_price: '',
    description: '',
    measurements: [] as MeasurementDefinition[],
    is_active: true
  });
  const [newMeasurement, setNewMeasurement] = useState({
    measurement_name: '',
    unit: 'cm'
  });

  // Shop Settings state
  const [shopSettingsForm, setShopSettingsForm] = useState<ShopSettingsFormData>({
    shop_name: '',
    shop_address: '',
    shop_phone: '',
    shop_email: '',
    logo_path: ''
  });
  const [shopSettingsLoading, setShopSettingsLoading] = useState(false);
  const [shopSettingsSaving, setShopSettingsSaving] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    sort_by: 'deleted_at',
    sort_order: 'DESC'
  });

  const loadDeletedCustomers = useCallback(async (params?: PaginationParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApi.getDeleted(params || paginationParams);
      if (response.status === 'success' && response.data) {
        setDeletedCustomers(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.message || 'Failed to load deleted customers');
      }
    } catch (err) {
      setError('Failed to load deleted customers');
      console.error('Load deleted customers error:', err);
    } finally {
      setLoading(false);
    }
  }, [paginationParams]);

  const searchDeletedCustomers = useCallback(async () => {
    const searchParams = {
      ...paginationParams,
      search: searchQuery,
      page: 1, // Reset to first page when searching
    };
    setPaginationParams(searchParams);
    await loadDeletedCustomers(searchParams);
  }, [searchQuery, paginationParams, loadDeletedCustomers]);

  const handlePageChange = (page: number) => {
    const newParams = { ...paginationParams, page };
    setPaginationParams(newParams);
    loadDeletedCustomers(newParams);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const newParams = { ...paginationParams, page: 1, limit: pageSize };
    setPaginationParams(newParams);
    loadDeletedCustomers(newParams);
  };

  const handleRestore = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowRestoreDialog(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedCustomer) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await customerApi.restore(selectedCustomer.id);
      if (response.status === 'success') {
        setSuccess('Customer restored successfully');
        await loadDeletedCustomers();
        setShowRestoreDialog(false);
        setSelectedCustomer(null);
        // Auto-clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to restore customer');
      }
    } catch (err) {
      setError('Failed to restore customer');
      console.error('Restore customer error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Load stitch items
  const loadStitchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await stitchItemApi.getAll();
      if (response.status === 'success' && response.data) {
        setStitchItems(response.data);
      } else {
        setError(response.message || 'Failed to load stitch items');
      }
    } catch (err) {
      setError('Failed to load stitch items');
      console.error('Load stitch items error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle stitch item form changes
  const handleStitchItemFormChange = (field: string, value: any) => {
    setStitchItemForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add measurement to stitch item
  const addMeasurement = () => {
    if (newMeasurement.measurement_name.trim()) {
      const measurementExists = stitchItemForm.measurements.some(
        m => m.measurement_name.toLowerCase() === newMeasurement.measurement_name.toLowerCase()
      );
      
      if (!measurementExists) {
        setStitchItemForm(prev => ({
          ...prev,
          measurements: [...prev.measurements, {
            measurement_name: newMeasurement.measurement_name.trim(),
            unit: newMeasurement.unit
          }]
        }));
        setNewMeasurement({
          measurement_name: '',
          unit: 'cm'
        });
      }
    }
  };

  // Remove measurement from stitch item
  const removeMeasurement = (index: number) => {
    setStitchItemForm(prev => ({
      ...prev,
      measurements: prev.measurements.filter((_, i) => i !== index)
    }));
  };

  // Open stitch item dialog
  const openStitchItemDialog = (item?: StitchItem) => {
    if (item) {
      setEditingStitchItem(item);
      setStitchItemForm({
        name: item.name,
        base_price: item.base_price.toString(),
        description: item.description || '',
        measurements: item.measurements || [],
        is_active: item.is_active
      });
    } else {
      setEditingStitchItem(null);
      setStitchItemForm({
        name: '',
        base_price: '',
        description: '',
        measurements: [],
        is_active: true
      });
    }
    setNewMeasurement({
      measurement_name: '',
      unit: 'cm'
    });
    setShowStitchItemDialog(true);
  };

  // Save stitch item
  const saveStitchItem = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const itemData = {
        name: stitchItemForm.name,
        base_price: parseFloat(stitchItemForm.base_price),
        description: stitchItemForm.description,
        measurements: stitchItemForm.measurements,
        is_active: stitchItemForm.is_active
      };

      let response;
      if (editingStitchItem) {
        response = await stitchItemApi.update(editingStitchItem.id, itemData);
      } else {
        response = await stitchItemApi.create(itemData);
      }

      console.log('API Response:', response);

      if (response.status === 'success') {
        setSuccess(editingStitchItem ? 'Stitch item updated successfully' : 'Stitch item created successfully');
        await loadStitchItems();
        setShowStitchItemDialog(false);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to save stitch item');
      }
    } catch (err) {
      setError('Failed to save stitch item');
      console.error('Save stitch item error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete stitch item
  const deleteStitchItem = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this stitch item?')) {
      try {
        setLoading(true);
        setError(null);
        const response = await stitchItemApi.delete(id);
        if (response.status === 'success') {
          setSuccess('Stitch item deleted successfully');
          await loadStitchItems();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError(response.message || 'Failed to delete stitch item');
        }
      } catch (err) {
        setError('Failed to delete stitch item');
        console.error('Delete stitch item error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 2) {
      // Load deleted customers when switching to deleted customers tab
      loadDeletedCustomers();
    } else if (newValue === 3) {
      // Load stitch items when switching to stitch items tab
      loadStitchItems();
    }
  };

  // Load shop settings
  const loadShopSettings = useCallback(async () => {
    try {
      setShopSettingsLoading(true);
      setError(null);
      const response = await settingsApi.getAll();
      if (response.status === 'success' && response.data) {
        // Convert ShopSettings to ShopSettingsFormData
        setShopSettingsForm({
          shop_name: response.data.shop_name,
          shop_address: response.data.shop_address,
          shop_phone: response.data.shop_phone,
          shop_email: response.data.shop_email,
          logo_path: response.data.logo_path
        });
      }
    } catch (error) {
      console.error('Error loading shop settings:', error);
      setError('Failed to load shop settings');
    } finally {
      setShopSettingsLoading(false);
    }
  }, []);

  // Save shop settings
  const saveShopSettings = async () => {
    try {
      setShopSettingsSaving(true);
      setError(null);
      
      // Convert ShopSettingsFormData to ShopSettings format
      const settingsToSave: Partial<ShopSettings> = {
        shop_name: shopSettingsForm.shop_name,
        shop_address: shopSettingsForm.shop_address,
        shop_phone: shopSettingsForm.shop_phone,
        shop_email: shopSettingsForm.shop_email,
        logo_path: shopSettingsForm.logo_path
      };
      
      const response = await settingsApi.update(settingsToSave);
      if (response.status === 'success') {
        setSuccess('Shop settings saved successfully');
        // Clear the form after successful save
        setShopSettingsForm({
          shop_name: '',
          shop_address: '',
          shop_phone: '',
          shop_email: '',
          logo_path: ''
        });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save shop settings');
      }
    } catch (error) {
      console.error('Error saving shop settings:', error);
      setError('Failed to save shop settings');
    } finally {
      setShopSettingsSaving(false);
    }
  };

  // Reset shop settings to default
  const resetShopSettings = async () => {
    if (window.confirm('Are you sure you want to reset shop settings to default?')) {
      try {
        setShopSettingsSaving(true);
        setError(null);
        const response = await settingsApi.update({});
        if (response.status === 'success') {
          setSuccess('Shop settings reset to default');
          loadShopSettings();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('Failed to reset shop settings');
        }
      } catch (error) {
        console.error('Error resetting shop settings:', error);
        setError('Failed to reset shop settings');
      } finally {
        setShopSettingsSaving(false);
      }
    }
  };

  // Handle shop settings form change
  const handleShopSettingsChange = (field: keyof ShopSettingsFormData, value: string) => {
    setShopSettingsForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle password form change
  const handlePasswordFormChange = (field: string, value: string) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Change password
  const changePassword = async () => {
    // Validate form
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    try {
      setPasswordChanging(true);
      setError(null);
      
      const response = await authApi.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      if (response.status === 'success') {
        setSuccess('Password changed successfully');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
      console.error('Change password error:', err);
    } finally {
      setPasswordChanging(false);
    }
  };

  // Reset password form
  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordForm(false);
    setError(null);
  };

  useEffect(() => {
    if (activeTab === 1) {
      // Load deleted customers when switching to deleted customers tab
      loadDeletedCustomers();
    } else if (activeTab === 2) {
      // Load stitch items when switching to stitch items tab
      loadStitchItems();
    } else if (activeTab === 3) {
      // Load shop settings when switching to shop settings tab
      loadShopSettings();
    }
    // Tab 0 (Subscription) and Tab 4 (Password) don't need API calls
  }, [activeTab, loadDeletedCustomers, loadStitchItems, loadShopSettings]);

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="Subscription" />
          <Tab label="Deleted Customers" />
          <Tab label="Stitch Items" />
          <Tab label="Shop Settings" />
          <Tab label="Password" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Subscription Information
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            View your subscription details and notifications.
          </Typography>
          <SubscriptionDisplay />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Deleted Customers
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Manage soft-deleted customers. You can restore them or permanently delete them.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search deleted customers by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    searchDeletedCustomers();
                  }
                }}
                size="small"
              />
              <Button
                variant="contained"
                onClick={searchDeletedCustomers}
                disabled={loading}
                size="small"
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
              {searchQuery && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchQuery('');
                    setPaginationParams({
                      page: 1,
                      limit: 10,
                      search: '',
                      sort_by: 'deleted_at',
                      sort_order: 'DESC'
                    });
                    loadDeletedCustomers();
                  }}
                  size="small"
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => loadDeletedCustomers()}
                disabled={loading}
                size="small"
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Deleted At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deletedCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {customer.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{customer.phone}</TableCell>
                        <TableCell>{customer.email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label="Deleted"
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Restore Customer">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleRestore(customer)}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {deletedCustomers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary">
                            {searchQuery ? 'No deleted customers found matching your search' : 'No deleted customers found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Stitch Items Management
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Create and manage your custom stitch items with their specific measurements.
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openStitchItemDialog()}
              sx={{ mb: 2 }}
            >
              Add New Stitch Item
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Base Price</TableCell>
                    <TableCell>Measurements</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stitchItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography variant="body2" color="textSecondary">
                            {item.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>₹{item.base_price}</TableCell>
                      <TableCell>
                        {item.measurements && item.measurements.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.measurements.map((measurement, index) => (
                              <Chip
                                key={index}
                                label={`${measurement.measurement_name} (${measurement.unit})`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No measurements
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.is_active ? 'Active' : 'Inactive'}
                          color={item.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Stitch Item">
                          <IconButton
                            size="small"
                            onClick={() => openStitchItemDialog(item)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Stitch Item">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteStitchItem(item.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {stitchItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">
                          No stitch items found. Create your first stitch item to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Shop Settings
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Configure your shop information and preferences.
            </Typography>

            {shopSettingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxWidth: 600 }}>
                <TextField
                  fullWidth
                  label="Shop Name"
                  value={shopSettingsForm.shop_name}
                  onChange={(e) => handleShopSettingsChange('shop_name', e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Shop Address"
                  value={shopSettingsForm.shop_address}
                  onChange={(e) => handleShopSettingsChange('shop_address', e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={shopSettingsForm.shop_phone}
                    onChange={(e) => handleShopSettingsChange('shop_phone', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={shopSettingsForm.shop_email}
                    onChange={(e) => handleShopSettingsChange('shop_email', e.target.value)}
                  />
                </Box>
                
                
                <TextField
                  fullWidth
                  label="Logo Path (URL)"
                  value={shopSettingsForm.logo_path}
                  onChange={(e) => handleShopSettingsChange('logo_path', e.target.value)}
                  sx={{ mb: 3 }}
                  helperText="Enter the URL or path to your shop logo"
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={saveShopSettings}
                    disabled={shopSettingsSaving}
                    startIcon={shopSettingsSaving ? <CircularProgress size={20} /> : null}
                  >
                    {shopSettingsSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={resetShopSettings}
                    disabled={shopSettingsSaving}
                    color="warning"
                  >
                    Reset to Default
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Password Settings
          </Typography>
          
          {/* Password Change Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Update your account password for security purposes.
            </Typography>
            
            {!showPasswordForm ? (
              <Button
                variant="contained"
                onClick={() => setShowPasswordForm(true)}
                startIcon={<EditIcon />}
              >
                Change Password
              </Button>
            ) : (
              <Box sx={{ maxWidth: 400 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordFormChange('currentPassword', e.target.value)}
                  margin="normal"
                  required
                />
                
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                  margin="normal"
                  required
                  helperText="Password must be at least 6 characters long"
                />
                
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                  margin="normal"
                  required
                />
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={changePassword}
                    disabled={passwordChanging}
                    startIcon={passwordChanging ? <CircularProgress size={20} /> : null}
                  >
                    {passwordChanging ? 'Changing...' : 'Change Password'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={resetPasswordForm}
                    disabled={passwordChanging}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
          
          <Typography color="textSecondary">
            Other general application settings will be available here in future updates.
          </Typography>
        </TabPanel>
      </Paper>

      {/* Stitch Item Dialog */}
      <Dialog open={showStitchItemDialog} onClose={() => setShowStitchItemDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStitchItem ? 'Edit Stitch Item' : 'Create New Stitch Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Item Name"
              value={stitchItemForm.name}
              onChange={(e) => handleStitchItemFormChange('name', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Base Price"
              type="number"
              value={stitchItemForm.base_price}
              onChange={(e) => handleStitchItemFormChange('base_price', e.target.value)}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={stitchItemForm.description}
              onChange={(e) => handleStitchItemFormChange('description', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Measurements
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Define the measurements required for this stitch item (e.g., Chest, Waist, Length, etc.)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Measurement Name"
                value={newMeasurement.measurement_name}
                onChange={(e) => setNewMeasurement(prev => ({
                  ...prev,
                  measurement_name: e.target.value
                }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addMeasurement();
                  }
                }}
              />
              <TextField
                select
                label="Unit"
                value={newMeasurement.unit}
                onChange={(e) => setNewMeasurement(prev => ({
                  ...prev,
                  unit: e.target.value
                }))}
                sx={{ minWidth: 100 }}
              >
                <MenuItem value="cm">cm</MenuItem>
                <MenuItem value="inch">inch</MenuItem>
                <MenuItem value="mm">mm</MenuItem>
                <MenuItem value="m">m</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                onClick={addMeasurement}
                disabled={!newMeasurement.measurement_name.trim()}
              >
                Add
              </Button>
            </Box>
            
            {stitchItemForm.measurements.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {stitchItemForm.measurements.map((measurement, index) => (
                  <Chip
                    key={index}
                    label={`${measurement.measurement_name} (${measurement.unit})`}
                    onDelete={() => removeMeasurement(index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStitchItemDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={saveStitchItem}
            variant="contained"
            disabled={submitting || !stitchItemForm.name || !stitchItemForm.base_price}
          >
            {submitting ? 'Saving...' : (editingStitchItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onClose={() => setShowRestoreDialog(false)}>
        <DialogTitle>Restore Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore <strong>{selectedCustomer?.name}</strong>? 
            This will make the customer available again in the customers list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRestoreDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {submitting ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;